<script setup lang="ts">
import { Send, Loader2, Square, ArrowUp, Wand2, Undo2, Mic, ImagePlus, Paperclip, Upload, X, Plus } from 'lucide-vue-next'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/components/ui/dropdown-menu'
import { ALLOWED_ACCEPT, uploadableImageName } from '~~/shared/allowedFileTypes'
import ModelSelector from './ModelSelector.vue'
import QraVersionSelector from './QraVersionSelector.vue'
import ProAttachTeaser from './chat/ProAttachTeaser.vue'
import UltraDictationMic from './chat/UltraDictationMic.vue'
import { useModelSelector } from '~/composables/useModelSelector'
import { useDragHighlight } from '~/composables/useDragHighlight'
import { usePromptImprove } from '~/composables/usePromptImprove'
import { hasMarcusPayload, parseDropPayload } from '~~/shared/documentDrop'
import type { ChatImage } from '~/types/web-chat'
import type { PromptImproveResponse } from '~~/shared/chat/promptImprove'
import { useComposerDictation } from '~/composables/useComposerDictation'
import { DEMO_BOOKING_URL } from '~~/shared/marketing/demoBooking'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  /** Whether the chat is busy (streaming or tool running) */
  busy?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Rotating placeholders (overrides placeholder when provided, non-empty, and input is empty/unfocused) */
  placeholders?: string[]
  /** Visual variant: 'compact' renders inline with buttons, 'box' uses styled card with footer */
  variant?: 'compact' | 'box'
  /** Textarea rows */
  rows?: number
  /** Disable the magic wand feature */
  disableWand?: boolean
  /** Hide the microphone button (e.g. Word add-in where mic API is unavailable) */
  hideMic?: boolean
  /** Block sending (e.g. a referenced doc is still processing) — disables send + shows a hint. */
  sendBlocked?: boolean
  /** Hint shown when sendBlocked is true. */
  sendBlockedHint?: string
  /**
   * Host handles documents (the web app chat): the composer then offers TWO distinct actions —
   * attach an existing document from the library (`pick-document`) and upload files from the device
   * (`upload-files`) — instead of the single image picker. Off in hosts with no document library of
   * their own (the Word add-in), which keep the inline image-vision picker.
   */
  documentActions?: boolean
  /**
   * Free-tier mode (`/chat`, MAR-1387): replaces the attachment trigger with a `PRO`-badged teaser
   * that opens an upgrade popover instead of a native file picker.
   */
  freeTier?: boolean
}>(), {
  busy: false,
  placeholder: 'Escribe un mensaje...',
  placeholders: () => [],
  variant: 'compact',
  rows: 1,
  disableWand: false,
  documentActions: false,
  freeTier: false,
})

const showProAttachPopover = ref(false)
const calcomUrl = computed(() => {
  const hookFn = (globalThis as Record<string, unknown>).useCalcomUrl
  if (typeof hookFn === 'function') {
    const res = (hookFn as () => { calcomUrl?: string | { value?: string } })()
    if (typeof res?.calcomUrl === 'string' && res.calcomUrl) return res.calcomUrl
    if (res?.calcomUrl && typeof res.calcomUrl === 'object' && typeof res.calcomUrl.value === 'string') {
      return res.calcomUrl.value
    }
  }
  try {
    const cfg = useRuntimeConfig()
    const url = (cfg?.public as Record<string, unknown> | undefined)?.demoBookingUrl
    if (typeof url === 'string' && url) return url
  } catch {
    // ignore when runtimeConfig is unavailable in isolated unit tests
  }
  return DEMO_BOOKING_URL
})

const emit = defineEmits<{
  send: [content: string, images?: ChatImage[]]
  cancel: []
  'drop-item': [item: { id: string; type: string; name: string; file_name: string | null }]
  /** Open the host's document picker (attach something already in the library). */
  'pick-document': []
  /** Files chosen from the device — the host uploads them exactly as a drop onto the chat does. */
  'upload-files': [files: File[]]
}>()

// The highlight ring; crossing the textarea or a toolbar icon inside the box must not clear it (MAR-1473).
const { active: dropOver, onDragLeave: onInputDragLeave } = useDragHighlight()

function onInputDragOver(e: DragEvent) {
  if (hasMarcusPayload(e.dataTransfer?.types)) {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer!.dropEffect = 'copy'
    dropOver.value = true
  }
}

function onInputDrop(e: DragEvent) {
  if (isSlowTier.value) {
    // Slow takes no attachments (MAR-1390): swallow the drop so an OS file does not bubble on to the
    // panel's upload handler either.
    e.preventDefault()
    e.stopPropagation()
    dropOver.value = false
    return
  }
  if (props.freeTier) {
    e.preventDefault()
    e.stopPropagation()
    dropOver.value = false
    showProAttachPopover.value = true
    return
  }
  if (!hasMarcusPayload(e.dataTransfer?.types)) return // Let native files bubble up to the global window handler

  e.preventDefault()
  e.stopPropagation()
  dropOver.value = false

  // Folders aren't expanded into chat context; each dropped document is added
  // individually (addToContext de-dupes), so multi-select drops work too.
  for (const item of parseDropPayload(e.dataTransfer)) {
    if (item.type === 'document') {
      emit('drop-item', { id: item.id, type: item.type, name: item.name ?? '', file_name: item.file_name ?? null })
    }
  }
}

const model = defineModel<string>({ default: '' })
const { modelId } = useModelSelector()
/**
 * Slow runs on the self-hosted Gemma through the queued `web-chat-gemma` runner (MAR-1390), which
 * carries no documents and no images: on Slow the composer neither offers nor accepts an attachment.
 */
const isSlowTier = computed(() => modelId.value === 'slow')
const { apiFetch } = useApi()
// The wand (MAR-408): snapshot + revert, and an inline error instead of a silent console.error.
const { improving, failed: improveFailed, canRevert, wandReady, improve, revert } = usePromptImprove({
  draft: model,
  enabled: () => !props.disableWand && !props.busy,
  request: async prompt => (await apiFetch<PromptImproveResponse>('/api/prompt/improve', {
    method: 'POST',
    body: { prompt, model: modelId.value },
  })).improved,
})
/** The wand doubles as the undo while the composer still holds the rewrite. */
const wandActive = computed(() => wandReady.value || canRevert.value)
const wandTitle = computed(() =>
  canRevert.value ? t('chat.improvePromptUndo') : wandReady.value ? t('chat.improvePrompt') : t('chat.improvePromptHint'),
)
function onWand() {
  if (canRevert.value) revert()
  else void improve()
}
const focused = ref(false)
const textareaRef = ref<HTMLTextAreaElement | null>(null)

// ── Speech-to-text (MAR-1423): live through the gateway on Ultra, the browser's own elsewhere ──
const { listening, liveConnecting, liveMicOn, liveStopping, showUltraMic, showPlainMic, dictationElapsedMs, toggleMic } = useComposerDictation(model, () => props.hideMic)

const showWand = computed(() => !props.disableWand && !props.freeTier && !props.busy)

// ── Image attachments (vision) ──
const images = ref<ChatImage[]>([])
const fileInputRef = ref<HTMLInputElement | null>(null)
const MAX_IMAGES = 6

function pickImages() {
  fileInputRef.value?.click()
}

/** Read a File into base64 (data: prefix stripped — the API wants the raw payload). */
function readImageFile(file: File): Promise<ChatImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve({ mimeType: file.type || 'image/png', data: comma >= 0 ? result.slice(comma + 1) : result, name: file.name })
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

async function addFiles(files: FileList | File[]) {
  for (const f of Array.from(files)) {
    if (!f.type.startsWith('image/')) continue
    if (images.value.length >= MAX_IMAGES) break
    try {
      images.value.push(await readImageFile(f))
    } catch (err) {
      console.error('Failed to read image:', err)
    }
  }
}

async function onFilesSelected(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files?.length) await addFiles(input.files)
  input.value = '' // allow re-picking the same file
}

/** Re-wrap a clipboard image under a name the upload path will accept. See `uploadableImageName`. */
function pastedImageAsFile(file: File): File {
  const name = uploadableImageName(file.name, file.type, `${t('chat.pastedImageName')}-${Date.now()}`)
  return name === file.name ? file : new File([file], name, { type: file.type })
}

function onPaste(e: ClipboardEvent) {
  const files = Array.from(e.clipboardData?.items || [])
    .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
    .map(it => it.getAsFile())
    .filter((f): f is File => !!f)
  if (!files.length) return
  e.preventDefault()
  if (isSlowTier.value) return
  if (props.freeTier) {
    showProAttachPopover.value = true
    return
  }

  // A PASTED IMAGE BECOMES A REAL DOCUMENT WHERE THERE IS A LIBRARY TO PUT IT IN (MAR-491).
  //
  // It used to land in the inline vision buffer, which the model can SEE but cannot NAME: the bytes
  // ride the turn, no `document_id` exists for them, and a tool that requires one — `convert_document`
  // — got handed the nearest id the model could see instead. That is the whole of MAR-491; the wrong
  // document was never a retrieval mistake, it was the only id on offer.
  //
  // So paste now takes the same road the Upload button and an OS-file drop already take: the host's
  // real upload path, which files it under "Chat uploads", indexes it, and attaches it as a context
  // chip with an id. THE COST IS REAL AND DELIBERATE — pasting used to create nothing and now creates
  // a library item that consumes indexing quota. That is the price of the picture being addressable,
  // and the chip plus the indexing queue both show it happening rather than doing it quietly.
  //
  // Hosts WITHOUT a document library (the Word add-in) keep the vision buffer: there is nowhere to
  // persist to, and vision-only is the right answer there rather than a degraded one.
  if (props.documentActions) {
    emit('upload-files', files.map(pastedImageAsFile))
    return
  }
  void addFiles(files)
}

function removeImage(i: number) {
  images.value.splice(i, 1)
}

// ── Upload from device (documentActions hosts) ──
// Separate input from the image one: this accepts every file type the library takes, and its files
// go to the host's real upload path (Documents + indexing), not the inline vision buffer. Images
// picked here are therefore uploaded as documents — same as dropping one on the chat, and, since
// MAR-491, same as pasting one. In a `documentActions` host EVERY way of attaching an image now
// produces a real document with an id; the vision buffer survives only where there is no library
// (the Word add-in), which is also the only host that still shows the image-picker button.
const uploadInputRef = ref<HTMLInputElement | null>(null)

function pickUploads() {
  uploadInputRef.value?.click()
}

function onUploadsSelected(e: Event) {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files || [])
  if (files.length) emit('upload-files', files)
  input.value = '' // allow re-picking the same file
}

/** The images this turn would carry: none on Slow, whatever was staged before switching to it. */
const stagedImages = computed(() => (isSlowTier.value ? [] : images.value))
const canSend = computed(() => (!!model.value.trim() || stagedImages.value.length > 0) && !improving.value && !props.sendBlocked)

function handleSend() {
  if (!canSend.value || props.busy) return
  const text = model.value.trim()
  emit('send', text, stagedImages.value.length ? stagedImages.value.slice() : undefined)
  model.value = ''
  images.value = []
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

function focus() {
  textareaRef.value?.focus()
}

defineExpose({ focus })

// ── Rotating placeholders ──
const rotatingIndex = ref(0)
const rotatingActive = computed(() =>
  props.placeholders.length > 1 && !model.value && !focused.value && !improving.value,
)
const rotatingText = computed(() =>
  props.placeholders.length > 0
    ? props.placeholders[rotatingIndex.value % props.placeholders.length]
    : props.placeholder,
)
let rotationTimer: ReturnType<typeof setInterval> | null = null
function startRotation() {
  if (rotationTimer || props.placeholders.length <= 1) return
  rotationTimer = setInterval(() => {
    if (!rotatingActive.value) return
    rotatingIndex.value = (rotatingIndex.value + 1) % props.placeholders.length
  }, 3500)
}
function stopRotation() {
  if (rotationTimer) {
    clearInterval(rotationTimer)
    rotationTimer = null
  }
}
onMounted(startRotation)
onUnmounted(stopRotation)

// Render a rotating overlay? Only when active AND there's actually a list to cycle.
const showRotatingOverlay = computed(() => rotatingActive.value)
// Effective static placeholder: suppress native when overlay is showing to avoid double-rendering.
const nativePlaceholder = computed(() =>
  props.placeholders.length > 1 ? '' : props.placeholder,
)
</script>

<template>
  <!-- ═══ COMPACT: textarea + controls row (sidebar, Word add-in) ═══ -->
  <div
    v-if="variant === 'compact'"
    data-chat-composer-dropzone
    class="flex flex-col gap-1.5"
    @dragover="onInputDragOver"
    @dragleave="onInputDragLeave"
    @drop="onInputDrop"
  >
    <input v-if="!freeTier && !isSlowTier" ref="fileInputRef" type="file" accept="image/*" multiple class="hidden" @change="onFilesSelected">
    <input v-if="documentActions && !freeTier && !isSlowTier" ref="uploadInputRef" type="file" :accept="ALLOWED_ACCEPT" multiple class="hidden" @change="onUploadsSelected">
    <div v-if="stagedImages.length" class="flex flex-wrap gap-2">
      <div v-for="(img, i) in stagedImages" :key="i" class="relative h-14 w-14 overflow-hidden rounded-md border border-border group">
        <img :src="`data:${img.mimeType};base64,${img.data}`" :alt="img.name || 'imagen'" class="h-full w-full object-cover">
        <button
          type="button"
          class="absolute top-0.5 right-0.5 h-4 w-4 flex items-center justify-center rounded-full bg-foreground/70 text-background opacity-0 transition-opacity group-hover:opacity-100"
          :title="t('chat.removeImage')"
          @click="removeImage(i)"
        >
          <X class="h-3 w-3" />
        </button>
      </div>
    </div>
    <div class="relative">
      <textarea
        ref="textareaRef"
        v-model="model"
        :placeholder="nativePlaceholder"
        :aria-label="placeholder"
        :rows="rows"
        :disabled="improving"
        autocomplete="chat-none"
        class="chat-textarea-autogrow chat-textarea-autogrow--compact flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
        @focus="focused = true"
        @blur="focused = false"
        @keydown="onKeydown"
        @paste="onPaste"
      />
      <Transition name="placeholder-fade">
        <span
          v-if="showRotatingOverlay"
          :key="rotatingText"
          aria-hidden="true"
          class="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground truncate max-w-[calc(100%-1.5rem)]"
        >
          {{ rotatingText }}
        </span>
      </Transition>
    </div>
    <p v-if="improveFailed" role="alert" class="text-[11px] text-destructive px-0.5 pb-1">{{ t('chat.improvePromptFailed') }}</p>
    <p v-if="sendBlocked && sendBlockedHint" class="flex items-center gap-1 text-[11px] text-warning-ink px-0.5 pb-1">
      <Loader2 class="h-3 w-3 animate-spin shrink-0" /> {{ sendBlockedHint }}
    </p>
    <div class="flex items-center justify-between gap-2">
      <div class="flex min-w-0 items-center gap-1.5">
        <ModelSelector />
        <QraVersionSelector v-if="!freeTier" />
        <slot name="footer-left" />
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <template v-if="isSlowTier" />
        <ProAttachTeaser v-else-if="freeTier" v-model:open="showProAttachPopover" :calcom-url="calcomUrl" trigger-class="hover:text-primary" />
        <!-- Two unmistakable actions: attach what's already in the library vs. upload from disk. -->
        <template v-else-if="documentActions">
          <button
            type="button"
            class="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary cursor-pointer transition-colors"
            :title="t('chat.attachDocument')"
            :aria-label="t('chat.attachDocument')"
            @click="emit('pick-document')"
          >
            <Paperclip class="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            class="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary cursor-pointer transition-colors"
            :title="t('chat.uploadFromDevice')"
            :aria-label="t('chat.uploadFromDevice')"
            @click="pickUploads"
          >
            <Upload class="h-3.5 w-3.5" />
          </button>
        </template>
        <button
          v-else
          type="button"
          class="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary cursor-pointer transition-colors"
          :title="t('chat.attachImage')"
          @click="pickImages"
        >
          <ImagePlus class="h-3.5 w-3.5" />
        </button>
        <UltraDictationMic
          v-if="showUltraMic"
          size="sm"
          :active="liveMicOn"
          :connecting="liveConnecting"
          :stopping="liveStopping"
          :elapsed-ms="dictationElapsedMs"
          @toggle="toggleMic"
        />
        <button
          v-if="showPlainMic"
          type="button"
          class="h-7 w-7 flex items-center justify-center rounded-md transition-colors"
          :class="listening
            ? 'text-destructive animate-pulse cursor-pointer'
            : 'text-muted-foreground hover:text-primary cursor-pointer'"
          :title="listening ? t('chat.stopDictation') : t('chat.dictateMessage')"
          @click="toggleMic"
        >
          <Mic class="h-3.5 w-3.5" />
        </button>
        <button
          v-if="showWand"
          type="button"
          class="h-7 w-7 flex items-center justify-center rounded-md transition-colors"
          :class="wandActive
            ? 'text-muted-foreground hover:text-primary cursor-pointer'
            : 'text-muted-foreground/30 cursor-default'"
          :title="wandTitle"
          :aria-label="wandTitle"
          :disabled="!wandActive"
          @click="onWand"
        >
          <Loader2 v-if="improving" class="h-3.5 w-3.5 animate-spin" />
          <Undo2 v-else-if="canRevert" class="h-3.5 w-3.5" />
          <Wand2 v-else class="h-3.5 w-3.5" />
        </button>
        <slot name="toolbar" />
        <Button
          v-if="busy"
          size="sm"
          variant="outline"
          class="h-7 w-7 p-0 shrink-0"
          :title="t('common.stop')"
          :aria-label="t('common.stop')"
          @click="emit('cancel')"
        >
          <Square class="h-3.5 w-3.5" />
        </Button>
        <Button
          v-else
          size="sm"
          class="h-7 w-7 p-0 shrink-0"
          :disabled="!canSend"
          :title="t('common.send')"
          :aria-label="t('common.send')"
          @click="handleSend"
        >
          <Send class="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
    <!-- Attached sections (the scope bar). The compact variant has no card, so the slot content
         carries its own chrome. -->
    <slot name="attached" />
  </div>

  <!-- ═══ BOX: the web chat's two-line composer (MAR-1057) ═══
       Line 1 is one card: "+", the input, the wand, dictation and send. Line 2, under it: the
       `footer-left` slot (scope), the `footer-center` slot (disclaimer) and the model. The `above`
       slot (the scope picker) renders over the card. -->
  <div v-else class="flex flex-col">
    <input v-if="!freeTier && !isSlowTier" ref="fileInputRef" type="file" accept="image/*" multiple class="hidden" @change="onFilesSelected">
    <input v-if="documentActions && !freeTier && !isSlowTier" ref="uploadInputRef" type="file" :accept="ALLOWED_ACCEPT" multiple class="hidden" @change="onUploadsSelected">
    <slot name="above" />
    <div
      data-chat-composer-dropzone
      class="chat-box"
      :class="{ 'chat-box--focused': focused, 'chat-box--drop': dropOver, '!overflow-visible': showProAttachPopover }"
      @dragover="onInputDragOver"
      @dragleave="onInputDragLeave"
      @drop="onInputDrop"
    >
      <div v-if="stagedImages.length" class="flex flex-wrap gap-2 px-3 pt-3">
        <div v-for="(img, i) in stagedImages" :key="i" class="relative h-14 w-14 overflow-hidden rounded-md border border-border group">
          <img :src="`data:${img.mimeType};base64,${img.data}`" :alt="img.name || 'imagen'" class="h-full w-full object-cover">
          <button
            type="button"
            class="absolute top-0.5 right-0.5 h-4 w-4 flex items-center justify-center rounded-full bg-foreground/70 text-background opacity-0 transition-opacity group-hover:opacity-100"
            :title="t('chat.removeImage')"
            @click="removeImage(i)"
          >
            <X class="h-3 w-3" />
          </button>
        </div>
      </div>
      <div class="flex items-end gap-0.5 p-1.5">
        <template v-if="isSlowTier" />
        <ProAttachTeaser v-else-if="freeTier" v-model:open="showProAttachPopover" class="self-center" :calcom-url="calcomUrl" trigger-class="hover:text-foreground" />
        <DropdownMenu v-else>
          <DropdownMenuTrigger as-child>
            <button type="button" class="chat-toolbar-btn text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer" :title="t('chat.attach')" :aria-label="t('chat.attach')">
              <Plus class="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" class="min-w-[15rem]">
            <template v-if="documentActions">
              <!-- Two unmistakable actions: attach what's already in the library vs. upload from disk. -->
              <DropdownMenuItem @select="emit('pick-document')"><Paperclip class="h-3.5 w-3.5" /> {{ t('chat.attachDocument') }}</DropdownMenuItem>
              <DropdownMenuItem @select="pickUploads"><Upload class="h-3.5 w-3.5" /> {{ t('chat.uploadFromDevice') }}</DropdownMenuItem>
            </template>
            <DropdownMenuItem @select="pickImages"><ImagePlus class="h-3.5 w-3.5" /> {{ t('chat.attachImage') }}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div class="relative min-w-0 flex-1 self-center">
          <textarea
            ref="textareaRef"
            v-model="model"
            :disabled="improving"
            :placeholder="nativePlaceholder"
            :aria-label="placeholder"
            :rows="rows"
            autocomplete="chat-none"
            class="chat-textarea"
            @focus="focused = true"
            @blur="focused = false"
            @keydown="onKeydown"
            @paste="onPaste"
          />
          <Transition name="placeholder-fade">
            <span v-if="showRotatingOverlay" :key="rotatingText" aria-hidden="true" class="chat-box-rotating-overlay">
              {{ rotatingText }}
            </span>
          </Transition>
        </div>
        <button
          v-if="showWand"
          type="button"
          class="chat-toolbar-btn"
          :class="wandActive ? 'text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer' : 'text-muted-foreground/40 cursor-default'"
          :title="wandTitle"
          :aria-label="wandTitle"
          :disabled="!wandActive"
          @click="onWand"
        >
          <Loader2 v-if="improving" class="h-4 w-4 animate-spin" />
          <Undo2 v-else-if="canRevert" class="h-4 w-4" />
          <Wand2 v-else class="h-4 w-4" />
        </button>
        <UltraDictationMic
          v-if="showUltraMic"
          :active="liveMicOn"
          :connecting="liveConnecting"
          :stopping="liveStopping"
          :elapsed-ms="dictationElapsedMs"
          @toggle="toggleMic"
        />
        <button
          v-if="showPlainMic"
          type="button"
          class="chat-toolbar-btn"
          :class="listening ? 'text-destructive animate-pulse cursor-pointer' : 'text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer'"
          :title="listening ? t('chat.stopDictation') : t('chat.dictateMessage')"
          :aria-label="listening ? t('chat.stopDictation') : t('chat.dictateMessage')"
          @click="toggleMic"
        >
          <Mic class="h-4 w-4" />
        </button>
        <button v-if="busy" class="chat-send chat-send--active" :title="t('common.stop')" :aria-label="t('common.stop')" @click="emit('cancel')">
          <Square class="h-3.5 w-3.5" />
        </button>
        <button
          v-else
          :disabled="!canSend"
          class="chat-send"
          :class="{ 'chat-send--active': canSend }"
          :title="t('common.send')"
          :aria-label="t('common.send')"
          @click="handleSend"
        >
          <Loader2 v-if="improving" class="h-4 w-4 animate-spin" />
          <ArrowUp v-else class="h-4 w-4" />
        </button>
      </div>
      <p v-if="improveFailed" role="alert" class="text-[11px] text-destructive px-3 pb-1.5">{{ t('chat.improvePromptFailed') }}</p>
      <p v-if="sendBlocked && sendBlockedHint" class="flex items-center gap-1 text-[11px] text-warning-ink px-3 pb-1.5">
        <Loader2 class="h-3 w-3 animate-spin shrink-0" /> {{ sendBlockedHint }}
      </p>
    </div>
    <div class="flex items-center gap-2 px-1 pt-1.5">
      <div class="flex min-w-0 shrink items-center"><slot name="footer-left" /></div>
      <div class="min-w-0 flex-1"><slot name="footer-center" /></div>
      <div class="flex shrink-0 items-center gap-1">
        <!-- MAR-1418: the jurisdiction picker, beside the model and QRA selectors. -->
        <slot name="footer-right" />
        <ModelSelector />
        <QraVersionSelector v-if="!freeTier" />
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "~/assets/css/tailwind.css";
.chat-box {
  display: flex;
  flex-direction: column;
  border-radius: 1.125rem;
  /* The "+" menu renders through a portal, so clipping the card is safe. */
  overflow: hidden;
  @apply bg-background border border-border;
  box-shadow:
    0 1px 3px color-mix(in oklab, var(--color-foreground) 4%, transparent),
    0 8px 24px color-mix(in oklab, var(--color-foreground) 3%, transparent);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
/* Focus is a soft ink border, not the accent (MAR-1057). */
.chat-box--focused {
  border-color: color-mix(in oklab, var(--color-muted-foreground) 55%, transparent);
  box-shadow:
    0 1px 3px color-mix(in oklab, var(--color-foreground) 5%, transparent),
    0 12px 32px color-mix(in oklab, var(--color-foreground) 6%, transparent);
}
.chat-box--drop {
  @apply border-primary bg-primary/[0.03];
}

/* Block, not the default inline-block: an inline textarea leaves a baseline gap under it that made
   its wrapper 7 px taller than the 32 px controls and lifted the text off their centre line. One line
   is 5 + 22 + 5 = 32 px, the controls' height. */
.chat-textarea {
  display: block;
  width: 100%;
  resize: none;
  field-sizing: content;
  background: transparent;
  padding: 0.3125rem 0.375rem;
  font-size: 0.9375rem;
  line-height: 1.375rem;
  @apply text-foreground;
  font-family: inherit;
  min-height: 2rem;
  max-height: 12rem;
  overflow-y: auto;
}

.chat-textarea-autogrow {
  field-sizing: content;
}
.chat-textarea-autogrow--compact {
  max-height: 8rem;
  overflow-y: auto;
}
.chat-textarea:focus { outline: none; }
.chat-textarea::placeholder {
  @apply text-muted-foreground;
  font-weight: 400;
}
.chat-textarea:disabled { opacity: 0.6; cursor: not-allowed; }

.chat-toolbar-btn {
  height: 2rem;
  width: 2rem;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  transition: color 0.15s ease;
}

.chat-send {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  margin-left: 0.375rem;
  border-radius: 0.625rem;
  flex: none;
  @apply bg-muted text-muted-foreground;
  cursor: not-allowed;
  transition: all 0.2s ease;
}
.chat-send--active {
  @apply bg-foreground text-background;
  cursor: pointer;
}
.chat-send--active:hover {
  @apply bg-foreground/85;
}

.chat-box-rotating-overlay {
  position: absolute;
  left: 0.375rem;
  top: 0.3125rem;
  pointer-events: none;
  font-size: 0.9375rem;
  line-height: 1.375rem;
  @apply text-muted-foreground;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 0.75rem);
}

.placeholder-fade-enter-active,
.placeholder-fade-leave-active {
  transition: opacity 250ms ease, transform 250ms ease;
}
.placeholder-fade-enter-from {
  opacity: 0;
  transform: translateY(4px);
}
.placeholder-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .placeholder-fade-enter-active,
  .placeholder-fade-leave-active {
    transition: none;
  }
  .placeholder-fade-enter-from,
  .placeholder-fade-leave-to {
    transform: none;
  }
}
</style>
